import type {
  AmbientEvaluatorInput,
  AmbientKind,
  AmbientOpportunity,
  AmbientSignal,
} from './ambientTypes'

const TEN_MIN = 10 * 60 * 1000
const FOURTEEN_DAYS = 14 * 24 * 60 * 60 * 1000

type HeuristicContext = AmbientEvaluatorInput

const makeOpportunity = (partial: Omit<AmbientOpportunity, 'id' | 'expiresAt'> & { expiresAt?: number }, now: number): AmbientOpportunity => {
  const primary = partial.targetRefs[0]?.id ?? 'global'
  const dayBucket = Math.floor(now / (24 * 60 * 60 * 1000))
  return {
    ...partial,
    id: `${partial.kind}:${primary}:${dayBucket}`,
    expiresAt: partial.expiresAt ?? now + TEN_MIN,
  }
}

const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0
  const rows = a.length + 1
  const cols = b.length + 1
  if (Math.abs(a.length - b.length) > 4) return 5
  const dp: number[] = new Array(cols).fill(0)
  for (let j = 0; j < cols; j++) dp[j] = j
  for (let i = 1; i < rows; i++) {
    let prev = dp[0] ?? 0
    dp[0] = i
    for (let j = 1; j < cols; j++) {
      const temp = dp[j] ?? 0
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[j] = Math.min((dp[j] ?? 0) + 1, (dp[j - 1] ?? 0) + 1, prev + cost)
      prev = temp
    }
  }
  return dp[cols - 1] ?? 0
}

const signalsOfKind = (signals: AmbientSignal[], kind: AmbientSignal['kind']) =>
  signals.filter((signal) => signal.kind === kind)

/** AC-DRAFT: AI just created ≥3 subtasks under one parent with no checklist items yet. */
const acDraft = (ctx: HeuristicContext): AmbientOpportunity[] => {
  const opportunities: AmbientOpportunity[] = []
  const completions = signalsOfKind(ctx.signals, 'AI_JOB_COMPLETED')
  completions.forEach((signal) => {
    const createdIds = Array.isArray(signal.payload?.createdIds) ? signal.payload.createdIds as string[] : []
    if (createdIds.length < 3) return
    const subtasks = createdIds
      .map((id) => ctx.workspaceElements.find((element) => element.id === id))
      .filter((element): element is NonNullable<typeof element> => element !== undefined && element.type === 'SUBTASK')
    if (subtasks.length < 3) return
    const parentIds = new Set(subtasks.map((element) => element.parentId).filter((id): id is string => Boolean(id)))
    parentIds.forEach((parentId) => {
      const parent = ctx.workspaceElements.find((element) => element.id === parentId)
      if (!parent) return
      const ownerChecklist = ctx.checklistCompletionByOwner?.[parentId]
      if (ownerChecklist && ownerChecklist.total > 0) return
      opportunities.push(makeOpportunity({
        kind: 'AC_DRAFT',
        title: 'Draft acceptance criteria?',
        reason: `You just made ${subtasks.length} subtasks under "${parent.title}".`,
        primaryAction: {
          label: 'Draft criteria',
          prompt: `Draft acceptance criteria for the ${subtasks.length} new subtasks under "${parent.title}". Keep each item concrete and testable.`,
        },
        secondaryAction: {
          label: 'Modify',
          prompt: `For each of the new subtasks under "${parent.title}", propose 2-3 acceptance criteria. Ask me first if you need scope.`,
        },
        targetRefs: [{ id: parent.id, type: parent.type, title: parent.title, board: parent.board }],
        score: 0.78,
      }, ctx.now))
    })
  })
  return opportunities
}

/** DEDUPE: two or more sibling nodes with similar names. */
const dedupe = (ctx: HeuristicContext): AmbientOpportunity[] => {
  const bySibling = new Map<string, typeof ctx.workspaceElements>()
  ctx.workspaceElements.forEach((element) => {
    const key = element.parentId ?? '__root__'
    const list = bySibling.get(key) ?? []
    list.push(element)
    bySibling.set(key, list)
  })
  const opportunities: AmbientOpportunity[] = []
  bySibling.forEach((siblings, key) => {
    if (siblings.length < 2) return
    const duplicates: typeof siblings = []
    for (let i = 0; i < siblings.length; i++) {
      for (let j = i + 1; j < siblings.length; j++) {
        const a = siblings[i]
        const b = siblings[j]
        if (!a || !b) continue
        if (levenshtein(a.title.toLowerCase(), b.title.toLowerCase()) < 3) {
          if (!duplicates.includes(a)) duplicates.push(a)
          if (!duplicates.includes(b)) duplicates.push(b)
        }
      }
    }
    if (duplicates.length < 2) return
    const primary = duplicates[0]
    if (!primary) return
    opportunities.push(makeOpportunity({
      kind: 'DEDUPE',
      title: 'Merge look-alike siblings?',
      reason: `${duplicates.length} nodes here look like duplicates — "${duplicates.map((element) => element.title).slice(0, 2).join('", "')}".`,
      primaryAction: {
        label: 'Review duplicates',
        prompt: `Compare the nodes ${duplicates.map((element) => `"${element.title}"`).join(', ')} (parent: ${key === '__root__' ? 'root' : key}) and suggest which to merge.`,
      },
      targetRefs: duplicates.map((element) => ({ id: element.id, type: element.type, title: element.title, board: element.board })),
      score: 0.62,
    }, ctx.now))
  })
  return opportunities
}

/** FOCUS-SYNC: focus session ended with edits made. */
const focusSync = (ctx: HeuristicContext): AmbientOpportunity[] => {
  const ended = signalsOfKind(ctx.signals, 'FOCUS_ENDED')
  return ended.map((signal) => {
    const editCount = typeof signal.payload?.editCount === 'number' ? signal.payload.editCount as number : 0
    if (editCount === 0) return null
    return makeOpportunity({
      kind: 'FOCUS_SYNC',
      title: 'Log your focus progress?',
      reason: `Focus session ended — ${editCount} edit${editCount === 1 ? '' : 's'} to log.`,
      primaryAction: {
        label: 'Summarize + log',
        prompt: `Summarize what I worked on in the last focus session (${editCount} edits) and append it to today's log.`,
      },
      targetRefs: signal.sourceRef ? [signal.sourceRef] : [],
      score: 0.55,
    }, ctx.now)
  }).filter((entry): entry is AmbientOpportunity => Boolean(entry))
}

/** STALE-BOSS: a top-level goal with no updates in 14 days but active children. */
const staleBoss = (ctx: HeuristicContext): AmbientOpportunity[] => {
  const opportunities: AmbientOpportunity[] = []
  ctx.workspaceElements
    .filter((element) => element.type === 'GOAL')
    .forEach((element) => {
      const lastUpdate = element.updatedAtIso ? new Date(element.updatedAtIso).getTime() : 0
      if (!lastUpdate || ctx.now - lastUpdate < FOURTEEN_DAYS) return
      const activeChildCount = ctx.workspaceElements.filter((child) => child.parentId === element.id).length
      if (activeChildCount === 0) return
      opportunities.push(makeOpportunity({
        kind: 'STALE_BOSS',
        title: `Refresh "${element.title}"?`,
        reason: `${activeChildCount} subtasks moved since the last update on "${element.title}".`,
        primaryAction: {
          label: 'Draft update',
          prompt: `Summarize what's changed under the goal "${element.title}" since ${new Date(lastUpdate).toISOString().slice(0, 10)} and draft a status refresh.`,
        },
        targetRefs: [{ id: element.id, type: element.type, title: element.title, board: element.board }],
        score: 0.5,
      }, ctx.now))
    })
  return opportunities
}

/** REJECTED-PIVOT: two consecutive proposal rejections. */
const rejectedPivot = (ctx: HeuristicContext): AmbientOpportunity[] => {
  const rejections = signalsOfKind(ctx.signals, 'PROPOSAL_REJECTED')
  if (rejections.length < 2) return []
  const [oldest, latest] = [rejections[rejections.length - 2], rejections[rejections.length - 1]]
  if (!oldest || !latest) return []
  const intent = typeof latest.payload?.intent === 'string' ? latest.payload.intent as string : 'this idea'
  return [makeOpportunity({
    kind: 'REJECTED_PIVOT',
    title: 'Try a different angle?',
    reason: `Two rejections in a row on "${intent}".`,
    primaryAction: {
      label: 'Sketch 3 options',
      prompt: `Sketch three alternative approaches for "${intent}" — cover a lean version, a premium version, and an unexpected angle.`,
    },
    targetRefs: latest.sourceRef ? [latest.sourceRef] : oldest.sourceRef ? [oldest.sourceRef] : [],
    score: 0.7,
  }, ctx.now)]
}

/** CHECKLIST-CLOSE: a checklist ≥90% complete. */
const checklistClose = (ctx: HeuristicContext): AmbientOpportunity[] => {
  const opportunities: AmbientOpportunity[] = []
  Object.entries(ctx.checklistCompletionByOwner ?? {}).forEach(([ownerId, completion]) => {
    if (completion.total < 3) return
    const ratio = completion.completed / completion.total
    if (ratio < 0.9 || ratio >= 1) return
    const remaining = completion.total - completion.completed
    const owner = ctx.workspaceElements.find((element) => element.id === ownerId)
    if (!owner) return
    opportunities.push(makeOpportunity({
      kind: 'CHECKLIST_CLOSE',
      title: `Wrap up "${completion.label || owner.title}"?`,
      reason: `${remaining} item${remaining === 1 ? '' : 's'} left in "${completion.label || owner.title}".`,
      primaryAction: {
        label: 'Nudge remaining',
        prompt: `List the remaining ${remaining} items in "${completion.label || owner.title}" and draft a nudge for each.`,
      },
      targetRefs: [{ id: owner.id, type: owner.type, title: owner.title, board: owner.board }],
      score: 0.58,
    }, ctx.now))
  })
  return opportunities
}

/** UPLOAD-LINK: new upload ingested, no timeline attachment yet. */
const uploadLink = (ctx: HeuristicContext): AmbientOpportunity[] => {
  const uploads = ctx.uploadsWithoutTimelineRef ?? []
  return uploads.map((upload) => makeOpportunity({
    kind: 'UPLOAD_LINK',
    title: `Attach "${upload.title}" to a node?`,
    reason: `New upload "${upload.title}" isn't linked to any timeline node yet.`,
    primaryAction: {
      label: 'Link upload',
      prompt: `Suggest the best timeline node to attach "${upload.title}" to based on its filename and my active work.`,
    },
    targetRefs: [{ id: upload.id, type: 'UPLOAD' as never, title: upload.title }],
    score: 0.45,
  }, ctx.now))
}

const HEURISTICS: Array<(ctx: HeuristicContext) => AmbientOpportunity[]> = [
  acDraft,
  focusSync,
  rejectedPivot,
  dedupe,
  checklistClose,
  staleBoss,
  uploadLink,
]

export const runHeuristics = (ctx: HeuristicContext): AmbientOpportunity[] =>
  HEURISTICS.flatMap((heuristic) => {
    try {
      return heuristic(ctx)
    } catch (error) {
      console.warn('[ambient] heuristic threw', error)
      return []
    }
  })

export const AMBIENT_KIND_LABELS: Record<AmbientKind, string> = {
  AC_DRAFT: 'Acceptance-criteria drafts',
  DEDUPE: 'Duplicate detection',
  FOCUS_SYNC: 'Focus session logs',
  STALE_BOSS: 'Stale goal reminders',
  REJECTED_PIVOT: 'Alternative angles after rejections',
  CHECKLIST_CLOSE: 'Checklist wrap-up nudges',
  UPLOAD_LINK: 'Upload linking',
}
